import { Controller } from "@hotwired/stimulus";
import { i18n } from "../lib/i18n";
import { bandWarnings, nextBandMin, resolveOverlaps } from "../lib/band_ranges";

// Operators whose filter value is ignored.
const NO_VALUE_OPERATORS = [
  "is_null",
  "is_not_null",
  "today",
  "this_week",
  "this_month",
  "this_quarter",
  "this_year",
];

/**
 * Base Builder Controller
 *
 * Shared functionality for report and metric builders.
 * Handles:
 * - Column management (add, remove, reorder via drag-drop)
 * - Column options (sort, aggregate)
 * - Filter management
 * - Grouping management
 * - Visualization type switching
 * - Chart and stat configuration
 * - Preview loading states
 * - Config sync and save operations
 *
 * Subclasses must implement:
 * - get entityId() - returns the report/metric ID
 * - get filtersFrameUrl() - returns URL for filters frame refresh
 * - get previewFrameId() - returns the preview frame element ID
 *
 * Subclasses may override:
 * - syncEntitySpecificConfigs() - for entity-specific config syncing
 */
export default class extends Controller {
  static targets = [
    "form",
    "nameField",
    "columnsConfig",
    "filtersConfig",
    "groupingsConfig",
    "sortsConfig",
    "visualizationConfig",
    "parametersField",
    "previewFrame",
    "filtersFrame",
    "filterPickerDropdown",
    "filterPickerBtn",
    "groupingsFrame",
    "groupingPickerDropdown",
    "groupingPickerBtn",
    "fieldPickerModal",
    "fieldSearch",
    "fieldList",
    "columnOptionsDropdown",
    "clearSortItem",
    "chartOptionsPanel",
    "statOptionsPanel",
    "statMeasurePanel",
    "groupByPanel",
    "vizTab",
    // Measure targets for stat/chart
    "measureType",
    "measureField",
    "measureFieldContainer",
    "measureSummary",
    // Scope targets (shared by both)
    "scopeType",
    "scopedOrganizationId",
    "scopedNetworkId",
    "followViewer",
    "scopeLabel",
    "scopeIcon",
    // Pagination targets
    "perPageSelect",
    "pagination",
    // Color picker targets
    "colorChips",
    // Column options targets
    "removeColumnItem",
  ];

  static values = {
    previewUrl: String,
    availableFields: Object,
    readOnly: { type: Boolean, default: false }
  };

  connect() {
    // Pagination state (available in both read-only and edit mode)
    this.currentPage = 1;
    this.currentPerPage = 100;
    if (this.hasPaginationTarget) this.paginationTargetConnected(this.paginationTarget);

    // Ephemeral sort state (for both read-only and quick sorting in edit mode)
    // Format: { field_key: 'field_name', direction: 'asc'|'desc' } or null
    this.ephemeralSort = null;

    // Ephemeral filters state (for quick filtering without persisting)
    // Format: Array of { field_key: 'field', operator: 'eq', value: 'val' }
    this.ephemeralFilters = [];

    // Sandbox mode: track unsaved changes
    this.hasUnsavedChanges = false;

    // In read-only mode, hidden form fields don't exist - initialize with empty arrays
    if (this.readOnlyValue) {
      this.columns = [];
      this.filters = [];
      this.groupings = [];
      this.sorts = [];
      this.visualization = { type: "table" };
      this.parameters = {};
      return; // No further setup needed for read-only mode
    }

    // Parse initial config from hidden fields
    this.parameters = this.hasParametersFieldTarget
      ? this.parseConfig(this.parametersFieldTarget.value) || {}
      : {};
    this.columns = this.parseConfig(this.columnsConfigTarget.value) || [];
    // Enrich columns with field metadata from available fields (field_type, aggregatable, persistent)
    this.enrichColumnsWithFieldMetadata();
    this.filters = this.parseConfig(this.filtersConfigTarget.value) || [];
    this.groupings = this.parseConfig(this.groupingsConfigTarget.value) || [];
    this.sorts = this.parseConfig(this.sortsConfigTarget.value) || [];
    this.visualization = this.parseConfig(
      this.visualizationConfigTarget.value,
    ) || { type: "table" };

    this.activeColumnIndex = null;
    this.draggedColumnIndex = null;

    // Store bound function for proper cleanup
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener("click", this.boundHandleOutsideClick);

    // Initialize panel visibility based on current visualization type
    this.updatePanelVisibility(this.visualization.type);

    // Initialize measure UI from existing columns
    this.initializeMeasureFromConfig();

    // Surface band gap/overlap warnings for groupings loaded from saved config
    this.renderBandWarnings();

    // Initialize save button as disabled (no unsaved changes on load)
    this.updateSaveButtonState();
  }

  disconnect() {
    if (this.boundHandleOutsideClick) {
      document.removeEventListener("click", this.boundHandleOutsideClick);
    }
    // Clear any pending timeouts
    if (this.previewTimeout) {
      clearTimeout(this.previewTimeout);
    }
  }

  parseConfig(value) {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  /**
   * Enrich columns with field metadata from available fields.
   * Backfills field_type, aggregatable, and persistent onto columns loaded
   * from persisted columns_config (which only stores minimal keys).
   */
  enrichColumnsWithFieldMetadata() {
    if (!this.availableFieldsValue) return;

    const directFields = this.availableFieldsValue.fields || [];
    const relationships = this.availableFieldsValue.relationships || [];

    this.columns = this.columns.map((col) => {
      let field;
      if (col.relationship_path) {
        const rel = relationships.find(
          (r) => r.key === col.relationship_path,
        );
        field = rel?.fields?.find((f) => f.key === col.field_key);
      } else {
        field = directFields.find((f) => f.key === col.field_key);
      }

      if (!field) return col;

      return {
        ...col,
        field_type: col.field_type || field.field_type || "string",
        aggregatable: col.aggregatable ?? field.aggregatable ?? false,
        persistent: field.persistent ?? col.persistent ?? false
      };
    });
  }

  /**
   * Get all fields from available fields (both direct fields and relationship fields) as a flat array.
   */
  getAllFieldsFlat() {
    const fields = this.availableFieldsValue.fields || [];
    const relationships = this.availableFieldsValue.relationships || [];
    const relationshipFields = relationships.flatMap((rel) => rel.fields || []);
    return [...fields, ...relationshipFields];
  }

  handleOutsideClick(event) {
    if (
      this.hasColumnOptionsDropdownTarget &&
      !this.columnOptionsDropdownTarget.contains(event.target) &&
      !event.target.closest("[data-column-index]")
    ) {
      this.hideColumnOptions();
    }
  }

  // ==========================================
  // Abstract methods - must be implemented by subclasses
  // ==========================================

  /**
   * Returns the entity ID (reportId or metricId)
   * @returns {number|null}
   */
  get entityId() {
    throw new Error("entityId must be implemented by subclass");
  }

  /**
   * Returns the URL for refreshing the filters frame
   * @returns {string}
   */
  get filtersFrameUrl() {
    throw new Error("filtersFrameUrl must be implemented by subclass");
  }

  /**
   * Returns the URL for refreshing the groupings frame
   * @returns {string}
   */
  get groupingsFrameUrl() {
    throw new Error("groupingsFrameUrl must be implemented by subclass");
  }

  /**
   * Returns the URL that lists former members for a group-history filter
   * @returns {string}
   */
  get groupHistoryMembersUrl() {
    throw new Error("groupHistoryMembersUrl must be implemented by subclass");
  }

  /**
   * Returns the preview frame element ID
   * @returns {string}
   */
  get previewFrameId() {
    throw new Error("previewFrameId must be implemented by subclass");
  }

  /**
   * Called during syncConfigs for entity-specific syncing
   * Override in subclass if needed
   */
  syncEntitySpecificConfigs() {
    // Optional override in subclass
  }

  // ==========================================
  // Parameter Management (parameterized data sources, e.g., survey_id)
  // ==========================================

  /**
   * Update a single parameter value (draft mode — not persisted until save).
   * Syncs to the hidden parameters field, refreshes the preview with the new
   * parameter applied, and reloads the filters and groupings frames so the
   * field picker, filter dropdowns, and group-by picker reflect the new
   * dynamic fields (e.g., q_<id> columns).
   */
  changeParameter(event) {
    const key = event.currentTarget.dataset.parameterKey;
    if (!key) return;

    const value = event.currentTarget.value;
    if (!this.parameters) this.parameters = {};
    if (value === "" || value === null || value === undefined) {
      delete this.parameters[key];
    } else {
      this.parameters[key] = value;
    }

    this.syncConfigs();
    this.refreshPreviewWithDraft();
    this.refreshFiltersFrame();
    this.refreshGroupingsFrame();
  }

  // ==========================================
  // Scope Management
  // ==========================================

  setScope(event) {
    event.preventDefault();
    const scopeType = event.currentTarget.dataset.scopeType;
    const scopeId = event.currentTarget.dataset.scopeId;
    const scopeLabel = event.currentTarget.dataset.scopeLabel;

    // Update hidden fields
    if (this.hasScopeTypeTarget) {
      this.scopeTypeTarget.value = scopeType;
    }

    // Clear both scope IDs first
    if (this.hasScopedOrganizationIdTarget) {
      this.scopedOrganizationIdTarget.value = "";
    }
    if (this.hasScopedNetworkIdTarget) {
      this.scopedNetworkIdTarget.value = "";
    }

    // User scope has two options sharing a type: one pinned to a person, one
    // that follows whoever is looking. The flag is what tells them apart.
    const followViewer =
      event.currentTarget.dataset.scopeFollowViewer === "true";
    if (this.hasFollowViewerTarget) {
      this.followViewerTarget.value = followViewer ? "1" : "0";
    }
    if (scopeType === "user" && followViewer && this.hasScopedUserIdTarget) {
      this.scopedUserIdTarget.value = "";
    }

    // Set the appropriate scope ID
    if (
      scopeType === "organization" &&
      scopeId &&
      this.hasScopedOrganizationIdTarget
    ) {
      this.scopedOrganizationIdTarget.value = scopeId;
    } else if (
      scopeType === "network" &&
      scopeId &&
      this.hasScopedNetworkIdTarget
    ) {
      this.scopedNetworkIdTarget.value = scopeId;
    }

    // Update the visible label
    if (this.hasScopeLabelTarget && scopeLabel) {
      this.scopeLabelTarget.textContent = scopeLabel;
    }

    // Update the scope icon
    const scopeIconName = event.currentTarget.dataset.scopeIcon;
    if (this.hasScopeIconTarget && scopeIconName) {
      this.scopeIconTarget.innerHTML = this.getScopeIconSvg(scopeIconName);
    } else {
      console.log(
        "NOT updating icon - hasScopeIconTarget:",
        this.hasScopeIconTarget,
        "scopeIconName:",
        scopeIconName,
      );
    }

    // Update active state in dropdown
    const dropdown = event.currentTarget.closest(".dropdown-content");
    if (dropdown) {
      dropdown
        .querySelectorAll("a")
        .forEach((a) => a.classList.remove("active"));
      event.currentTarget.classList.add("active");
    }

    // Refresh preview with new scope (sandbox mode - not persisted until save)
    this.refreshPreviewWithDraft();

    // Refresh filters frame to update lookup URLs with new scope context
    // This ensures foreign key filter dropdowns show options for the new scope
    this.refreshFiltersFrame();

    // Close dropdown by blurring
    document.activeElement.blur();
  }

  /**
   * Returns the SVG markup for a scope icon
   */
  getScopeIconSvg(iconName) {
    const icons = {
      "user-outline":
        '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 0 0-16 0"/></svg>',
      "shield-user":
        '<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="M6.376 18.91a6 6 0 0 1 11.249.003"/><circle cx="12" cy="11" r="4"/></svg>',
      building:
        '<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-building-2"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>',
      network:
        '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 12C21.5 13.3807 20.3807 14.5 19 14.5C17.6193 14.5 16.5 13.3807 16.5 12C16.5 10.6193 17.6193 9.5 19 9.5C20.3807 9.5 21.5 10.6193 21.5 12Z"/><path d="M13.5 4C13.5 4.82843 12.8284 5.5 12 5.5C11.1716 5.5 10.5 4.82843 10.5 4C10.5 3.17157 11.1716 2.5 12 2.5C12.8284 2.5 13.5 3.17157 13.5 4Z"/><path d="M12.5 11.5C12.5 12.3284 11.8284 13 11 13C10.1716 13 9.5 12.3284 9.5 11.5C9.5 10.6716 10.1716 10 11 10C11.8284 10 12.5 10.6716 12.5 11.5Z"/><path d="M6.5 7.5C6.5 8.60457 5.60457 9.5 4.5 9.5C3.39543 9.5 2.5 8.60457 2.5 7.5C2.5 6.39543 3.39543 5.5 4.5 5.5C5.60457 5.5 6.5 6.39543 6.5 7.5Z"/><path d="M10.5 19.5C10.5 20.6046 9.60457 21.5 8.5 21.5C7.39543 21.5 6.5 20.6046 6.5 19.5C6.5 18.3954 7.39543 17.5 8.5 17.5C9.60457 17.5 10.5 18.3954 10.5 19.5Z"/><path d="M13.5 5L17.5 10M14.5 15.5L10.5 18.5M8 17.5L5 9.5M6.31298 6.65431L10.5 4.5M12.5 11.5L16.505 11.8443"/><path d="M12 5.5L11 10"/></svg>',
      database:
        '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/></svg>'
    };
    return icons[iconName] || icons["building"];
  }

  // ==========================================
  // Visualization Type
  // ==========================================

  setVisualization(event) {
    const type = event.currentTarget.dataset.type;
    this.visualization.type = type;

    // Update tab UI
    this.vizTabTargets.forEach((tab) => {
      tab.classList.toggle("tab-active", tab.dataset.type === type);
    });

    // Show/hide panels based on visualization type
    this.updatePanelVisibility(type);

    // For chart: ensure we have a measure configured and sync the UI dropdowns
    if (type === "chart" && this.visualization.chart_type !== "scatter") {
      this.ensureChartHasAggregate();
      this.initializeMeasureFromConfig();
    }

    this.syncConfigs();
    this.refreshPreviewWithDraft();
  }

  /**
   * Update panel visibility based on visualization type
   * - Stat: show measure + stat display options, hide groupBy
   * - Chart: show groupBy + chart options, hide measure (or optional)
   * - Table: show groupBy, hide measure/chart/stat options
   */
  updatePanelVisibility(type) {
    const scatter = type === "chart" && this.visualization.chart_type === "scatter";
    // Measure panel (for stat and chart - both need measure config)
    if (this.hasStatMeasurePanelTarget) {
      this.statMeasurePanelTarget.classList.toggle(
        "hidden",
        scatter || (type !== "stat" && type !== "chart"),
      );
    }

    // Stat display options (only for stat)
    if (this.hasStatOptionsPanelTarget) {
      this.statOptionsPanelTarget.classList.toggle("hidden", type !== "stat");
    }

    // Chart options (only for chart)
    if (this.hasChartOptionsPanelTarget) {
      this.chartOptionsPanelTarget.classList.toggle("hidden", type !== "chart");
    }

    // Group By panel (for chart and table, not stat)
    if (this.hasGroupByPanelTarget) {
      this.groupByPanelTarget.classList.toggle("hidden", type === "stat" || scatter);
    }

    this.element.querySelectorAll("[data-chart-type-panel]").forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.chartTypePanel !== this.visualization.chart_type);
    });
    this.element.querySelectorAll("[data-chart-hide-for]").forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.chartHideFor === this.visualization.chart_type);
    });
  }

  updateChartType(event) {
    this.visualization.chart_type = event.currentTarget.value;
    this.updatePanelVisibility(this.visualization.type);
    this.syncConfigs();
    this.refreshPreviewWithDraft();
  }

  updateChartConfig() {
    const showLegend = this.element.querySelector("[data-chart-show-legend]");
    const colorScheme = this.element.querySelector("[data-chart-color-scheme]");
    const customColors = this.element.querySelector(
      "[data-chart-custom-colors]",
    );

    if (showLegend) this.visualization.show_legend = showLegend.checked;
    if (colorScheme) this.visualization.color_scheme = colorScheme.value;
    if (customColors) this.visualization.custom_colors = customColors.value;
    this.element.querySelectorAll("[data-chart-axis]").forEach((field) => {
      this.visualization[field.dataset.chartAxis] = field.value;
    });

    this.syncConfigs();
    this.refreshPreviewWithDraft();
  }

  // Color chip methods for custom color picker UI
  applyColorPreset(event) {
    // Support both button (data-colors) and select (value) approaches
    const colorsStr = event.currentTarget.dataset.colors || event.target.value;
    if (!colorsStr || !this.hasColorChipsTarget) return;

    const colors = colorsStr.split(",").map((c) => c.trim());
    this.renderColorChips(colors);
    this.syncColorChipsToHiddenInput();
    this.updateChartConfig();
  }

  addColorChip() {
    if (!this.hasColorChipsTarget) return;

    // Default to a nice blue color
    const defaultColor = "#3b82f6";
    const container = this.colorChipsTarget;
    const addButton = container.querySelector('[data-action*="addColorChip"]');

    const chip = this.createColorChipElement(defaultColor);
    container.insertBefore(chip, addButton);

    this.syncColorChipsToHiddenInput();
    this.updateChartConfig();
  }

  removeColorChip(event) {
    const chip = event.target.closest(".color-chip");
    if (chip) {
      chip.remove();
      this.syncColorChipsToHiddenInput();
      this.updateChartConfig();
    }
  }

  updateColorChip() {
    this.syncColorChipsToHiddenInput();
    this.updateChartConfig();
  }

  // Helper: sync color chips to hidden input
  syncColorChipsToHiddenInput() {
    if (!this.hasColorChipsTarget) return;

    const chips = this.colorChipsTarget.querySelectorAll(
      '.color-chip input[type="color"]',
    );
    const colors = Array.from(chips).map((input) => input.value);
    const hiddenInput = this.element.querySelector(
      "[data-chart-custom-colors]",
    );
    if (hiddenInput) {
      hiddenInput.value = colors.join(", ");
    }
  }

  // Helper: render color chips from an array of colors
  renderColorChips(colors) {
    if (!this.hasColorChipsTarget) return;

    const container = this.colorChipsTarget;
    const addButton = container.querySelector('[data-action*="addColorChip"]');

    // Remove existing chips
    container.querySelectorAll(".color-chip").forEach((chip) => chip.remove());

    // Add new chips
    colors.forEach((color) => {
      const chip = this.createColorChipElement(color);
      container.insertBefore(chip, addButton);
    });
  }

  // Helper: create a color chip DOM element
  createColorChipElement(color) {
    const controllerName = this.identifier;

    const chip = document.createElement("div");
    chip.className =
      "color-chip flex items-center gap-1 pl-1 pr-2 py-1 rounded-lg border bg-base-100 shadow-sm";

    const colorInput = document.createElement("input");
    colorInput.type = "color";
    colorInput.value = color;
    colorInput.className = "w-6 h-6 rounded cursor-pointer border-0 p-0";
    colorInput.style.backgroundColor = color;
    colorInput.dataset.action = `input->${controllerName}#updateColorChip`;

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className =
      "text-base-content/50 hover:text-error text-sm leading-none";
    removeBtn.dataset.action = `click->${controllerName}#removeColorChip`;
    removeBtn.title = i18n.t('base_builder.remove_color');
    removeBtn.innerHTML = "&times;";

    chip.appendChild(colorInput);
    chip.appendChild(removeBtn);

    return chip;
  }

  updateStatConfig() {
    const title = this.element.querySelector("[data-stat-title]");
    const prefix = this.element.querySelector("[data-stat-prefix]");
    const suffix = this.element.querySelector("[data-stat-suffix]");
    const icon = this.element.querySelector("[data-stat-icon]");
    const color = this.element.querySelector("[data-stat-color]");

    if (title) this.visualization.title = title.value;
    if (prefix) this.visualization.prefix = prefix.value;
    if (suffix) this.visualization.suffix = suffix.value;
    if (icon) this.visualization.icon = icon.value;
    if (color) this.visualization.color = color.value;

    this.syncConfigs();
    this.refreshPreviewWithDraft();
  }

  // Switch to stat view (called from prompt when aggregate without grouping)
  switchToStatView() {
    this.visualization.type = "stat";

    // Update tab UI
    this.vizTabTargets.forEach((tab) => {
      tab.classList.toggle("tab-active", tab.dataset.type === "stat");
    });

    // Update panel visibility
    this.updatePanelVisibility("stat");

    this.syncConfigs();
    this.refreshPreviewWithDraft();
  }

  // ==========================================
  // Measure Configuration (for Stat/Chart)
  // ==========================================

  /**
   * Called when the measure type dropdown changes
   */
  measureTypeChanged(event) {
    const measureType = event.currentTarget.value;

    // Show/hide field selector based on measure type
    // "count" doesn't need a field, others do
    if (this.hasMeasureFieldContainerTarget) {
      const needsField = measureType !== "count";
      this.measureFieldContainerTarget.classList.toggle("hidden", !needsField);
    }

    this.filterMeasureFieldOptions(measureType);

    // Update measure in visualization_config (not columns_config)
    this.updateMeasureConfig();
    this.syncConfigs();
    this.refreshPreviewWithDraft();
  }

  /**
   * Restrict the field list to fields whose type supports the chosen aggregate.
   * Each option carries the server's available_aggregates for that field, so a
   * foreign key never ends up under "Average of...".
   */
  filterMeasureFieldOptions(measureType) {
    if (!this.hasMeasureFieldTarget) return;

    let firstAllowed = null;

    Array.from(this.measureFieldTarget.options).forEach((option) => {
      const allowed = (option.dataset.availableAggregates || "")
        .split(" ")
        .includes(measureType);

      option.hidden = !allowed;
      option.disabled = !allowed;
      if (allowed && !firstAllowed) firstAllowed = option;
    });

    if (this.measureFieldTarget.selectedOptions[0]?.disabled) {
      this.measureFieldTarget.value = firstAllowed ? firstAllowed.value : "";
    }
  }

  /**
   * Called when the measure field dropdown changes
   */
  measureFieldChanged() {
    this.updateMeasureConfig();
    this.syncConfigs();
    this.refreshPreviewWithDraft();
  }

  /**
   * Update measure settings in visualization_config
   * This keeps measure config separate from columns_config so table isn't affected
   * Used by both stat and chart visualizations
   */
  updateMeasureConfig() {
    if (!this.hasMeasureTypeTarget) return;

    const measureType = this.measureTypeTarget.value || "count";
    let fieldKey = "id"; // default for count

    // If measure needs a specific field, get it from the field selector
    if (measureType !== "count" && this.hasMeasureFieldTarget) {
      fieldKey = this.measureFieldTarget.value || "id";
    }

    // Store in visualization_config - executor will use this for stat and chart formats
    this.visualization.measure_type = measureType;
    this.visualization.measure_field = fieldKey;

    // Update measure summary text if present
    this.updateMeasureSummary(measureType, fieldKey);
  }

  /**
   * Ensure chart has a measure configured (defaults to count)
   * Stores in visualization_config, NOT in columns - columns stay unchanged
   * This prevents table view from being affected by chart/stat measure config
   */
  ensureChartHasAggregate() {
    // Check if measure is already configured in visualization_config
    if (this.visualization.measure_type) {
      return; // Already configured
    }

    // Check if there's an existing aggregate in columns (backwards compat)
    const existingAggregate = this.columns.find((c) => c.aggregate);
    if (existingAggregate) {
      // Migrate to visualization_config
      this.visualization.measure_type = existingAggregate.aggregate;
      this.visualization.measure_field = existingAggregate.field_key;
      return;
    }

    // Default to count
    this.visualization.measure_type = "count";
    this.visualization.measure_field = "id";
  }

  /**
   * Update the measure summary text (shown in collapsed measure section)
   */
  updateMeasureSummary(measureType, fieldKey) {
    if (!this.hasMeasureSummaryTarget) return;

    const labels = {
      count: i18n.t('base_builder.count_records'),
      count_distinct: i18n.t('base_builder.count_distinct', { field: fieldKey }),
      sum: i18n.t('base_builder.sum_of', { field: fieldKey }),
      avg: i18n.t('base_builder.avg_of', { field: fieldKey }),
      min: i18n.t('base_builder.min_of', { field: fieldKey }),
      max: i18n.t('base_builder.max_of', { field: fieldKey })
    };

    this.measureSummaryTarget.textContent =
      labels[measureType] ||
      (this.hasMeasureTypeTarget && this.measureTypeTarget.selectedOptions[0]?.textContent) ||
      i18n.t('base_builder.count_records');
  }

  /**
   * Initialize measure UI from visualization_config
   * Called on connect to sync measure panel with existing data
   */
  initializeMeasureFromConfig() {
    // Read from visualization_config (preferred) or fallback to columns_config for backwards compat
    let measureType = this.visualization.measure_type;
    let measureField = this.visualization.measure_field;

    // Backwards compatibility: if not in visualization_config, check columns_config
    if (!measureType) {
      const aggColumn = this.columns.find((c) => c.aggregate);
      if (aggColumn) {
        measureType = aggColumn.aggregate;
        measureField = aggColumn.field_key;
      }
    }

    if (measureType && this.hasMeasureTypeTarget) {
      this.measureTypeTarget.value = measureType;

      // Show field selector if needed
      if (this.hasMeasureFieldContainerTarget) {
        const needsField = measureType !== "count";
        this.measureFieldContainerTarget.classList.toggle(
          "hidden",
          !needsField,
        );
      }

      // Set field value if present
      if (
        measureType !== "count" &&
        this.hasMeasureFieldTarget &&
        measureField
      ) {
        this.measureFieldTarget.value = measureField;
      }

      // Repairs a saved measure whose field no longer supports the aggregate
      this.filterMeasureFieldOptions(measureType);

      this.updateMeasureSummary(
        measureType,
        (this.hasMeasureFieldTarget && this.measureFieldTarget.value) ||
          measureField ||
          "id",
      );
    }
  }

  // Scroll to and highlight Group By section
  openGroupByPicker() {
    const groupBySection = this.element.querySelector("[data-groupings-list]");
    if (groupBySection) {
      groupBySection.scrollIntoView({ behavior: "smooth", block: "center" });
      // Flash highlight effect
      groupBySection.classList.add("ring-2", "ring-primary", "ring-offset-2");
      setTimeout(() => {
        groupBySection.classList.remove(
          "ring-2",
          "ring-primary",
          "ring-offset-2",
        );
      }, 2000);
    }
  }

  // ==========================================
  // Field Picker
  // ==========================================

  openFieldPicker() {
    if (this.hasFieldPickerModalTarget) {
      this.fieldPickerModalTarget.showModal();
    }
  }

  closeFieldPicker() {
    if (this.hasFieldPickerModalTarget) {
      this.fieldPickerModalTarget.close();
    }
    if (this.hasFieldSearchTarget) {
      this.fieldSearchTarget.value = "";
      this.filterFields({ target: { value: "" } });
    }
    // Preview is already updated when fields are added, no need to refresh again
  }

  filterFields(event) {
    const searchTerm = event.target.value.toLowerCase();

    // Try fieldList target first (metric builder), then fall back to element query
    const container = this.hasFieldListTarget
      ? this.fieldListTarget
      : this.element;

    // Use [data-field-item] selector to only match field picker buttons, not table column headers
    const buttons = container.querySelectorAll("[data-field-item]");
    const categories = container.querySelectorAll(
      "[data-category], [data-field-category]",
    );

    buttons.forEach((button) => {
      const label = button.dataset.fieldLabel?.toLowerCase() || "";
      const key = button.dataset.fieldKey?.toLowerCase() || "";
      const matches = label.includes(searchTerm) || key.includes(searchTerm);
      button.classList.toggle("hidden", !matches);
    });

    // Hide empty categories
    categories.forEach((category) => {
      const visibleButtons = category.querySelectorAll(
        "[data-field-item]:not(.hidden)",
      );
      category.classList.toggle("hidden", visibleButtons.length === 0);
    });
  }

  selectField(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (button.disabled) return;

    const field = {
      field_key: button.dataset.fieldKey,
      visible: true,
      relationship_path:
        button.dataset.relationshipKey ||
        button.dataset.relationshipPath ||
        null,
      field_type: button.dataset.fieldType || "string",
      aggregatable: button.dataset.aggregatable === "true",
      persistent: button.dataset.persistent === "true"
    };

    const existingIndex = this.columns.findIndex(
      (col) =>
        col.field_key === field.field_key &&
        (col.relationship_path || null) === field.relationship_path,
    );

    if (existingIndex >= 0) {
      this.columns[existingIndex] = {
        ...this.columns[existingIndex],
        ...field,
        visible: true
      };
    } else {
      this.columns.push(field);
    }

    this.syncConfigs();

    // Mark button as added without closing the modal
    button.disabled = true;
    button.classList.add("opacity-50");

    // Add check icon if not already present
    if (!button.querySelector(".check-icon")) {
      const checkIcon = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "svg",
      );
      checkIcon.setAttribute("class", "w-4 h-4 text-primary check-icon");
      checkIcon.setAttribute("viewBox", "0 0 24 24");
      checkIcon.setAttribute("fill", "none");
      checkIcon.setAttribute("stroke", "currentColor");
      checkIcon.setAttribute("stroke-width", "2");
      checkIcon.innerHTML = '<polyline points="20 6 9 17 4 12"></polyline>';
      button.appendChild(checkIcon);
    }

    // Refresh preview with draft config (sandbox mode)
    this.refreshPreviewWithDraft();
  }

  /**
   * Sync field picker buttons to reflect current column selection state
   * Called after columns are added or removed to update the modal UI
   */
  syncFieldPickerState() {
    const fieldPickerFrame = document.getElementById("field_picker_frame");
    if (!fieldPickerFrame) return;

    const buttons = fieldPickerFrame.querySelectorAll("[data-field-item]");

    buttons.forEach((button) => {
      const fieldKey = button.dataset.fieldKey;
      const relationshipPath =
        button.dataset.relationshipKey ||
        button.dataset.relationshipPath ||
        null;

      // Check if this field+relationship is currently in columns
      const isSelected = this.columns.some(
        (col) =>
          col.field_key === fieldKey &&
          (col.relationship_path || null) === relationshipPath &&
          col.visible !== false,
      );

      if (isSelected) {
        // Mark as added
        button.disabled = true;
        button.classList.add("opacity-50");
        if (!button.querySelector(".check-icon")) {
          const checkIcon = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "svg",
          );
          checkIcon.setAttribute("class", "w-4 h-4 text-primary check-icon");
          checkIcon.setAttribute("viewBox", "0 0 24 24");
          checkIcon.setAttribute("fill", "none");
          checkIcon.setAttribute("stroke", "currentColor");
          checkIcon.setAttribute("stroke-width", "2");
          checkIcon.innerHTML = '<polyline points="20 6 9 17 4 12"></polyline>';
          button.appendChild(checkIcon);
        }
      } else {
        // Un-mark as added
        button.disabled = false;
        button.classList.remove("opacity-50");
        const checkIcon = button.querySelector(".check-icon");
        if (checkIcon) checkIcon.remove();
      }
    });
  }

  // ==========================================
  // Column Management
  // ==========================================

  removeColumn(event) {
    event.preventDefault();
    event.stopPropagation();
    const index = parseInt(event.currentTarget.dataset.index);

    if (isNaN(index) || index < 0 || index >= this.columns.length) return;

    // Don't allow removal of persistent columns
    const col = this.columns[index];
    if (col?.persistent === true) return;

    this.columns.splice(index, 1);
    this.syncConfigs();
    this.refreshPreviewWithDraft();
    this.syncFieldPickerState();
  }

  showColumnOptions(event) {
    // Don't show if clicking the remove button
    if (event.target.closest('[data-action*="removeColumn"]')) return;

    const th = event.currentTarget;
    const index = parseInt(th.dataset.columnIndex);

    if (isNaN(index)) return;

    this.activeColumnIndex = index;

    if (!this.hasColumnOptionsDropdownTarget) return;

    const dropdown = this.columnOptionsDropdownTarget;
    const rect = th.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Check if this column is currently sorted
    const col = this.columns[index];
    const isSorted =
      col && this.sorts.some((s) => s.field_key === col.field_key);

    // Show/hide the "Clear Sort" option
    if (this.hasClearSortItemTarget) {
      this.clearSortItemTarget.classList.toggle("hidden", !isSorted);
    }

    // Position dropdown below the header (using fixed positioning)
    let top = rect.bottom + 4;
    let left = rect.left;

    // Adjust if dropdown would go off right edge
    const dropdownWidth = 180;
    if (left + dropdownWidth > viewportWidth) {
      left = viewportWidth - dropdownWidth - 8;
    }

    // Adjust if dropdown would go off bottom edge
    const dropdownHeight = 320;
    if (top + dropdownHeight > viewportHeight) {
      top = rect.top - dropdownHeight - 4;
    }

    dropdown.style.top = `${top}px`;
    dropdown.style.left = `${left}px`;
    dropdown.classList.remove("hidden");

    // Show/hide quick filter options based on field type
    this.updateQuickFilterVisibility(col?.field_type || "string");

    // Show/hide aggregate options based on field type, aggregatable status, and current aggregate
    this.updateAggregateVisibility(
      col?.field_type || "string",
      col?.aggregatable ?? false,
      col?.aggregate,
    );

    // Show/hide date format options based on field type and current format
    this.updateDateFormatVisibility(
      col?.field_type || "string",
      col?.date_format || null,
    );

    // Show/hide remove column option based on persistent flag
    if (this.hasRemoveColumnItemTarget) {
      this.removeColumnItemTarget.classList.toggle(
        "hidden",
        col?.persistent === true,
      );
    }

    event.stopPropagation();
  }

  /**
   * Show/hide quick filter menu items based on the column's field type
   */
  updateQuickFilterVisibility(fieldType) {
    if (!this.hasColumnOptionsDropdownTarget) return;

    const dropdown = this.columnOptionsDropdownTarget;
    const quickFilterItems = dropdown.querySelectorAll(
      "[data-quick-filter-types]",
    );

    quickFilterItems.forEach((item) => {
      const allowedTypes = item.dataset.quickFilterTypes;
      if (
        allowedTypes === "all" ||
        allowedTypes.split(" ").includes(fieldType)
      ) {
        item.classList.remove("hidden");
      } else {
        item.classList.add("hidden");
      }
    });
  }

  /**
   * Show/hide aggregate menu items based on the column's field type, aggregatable status, and current aggregate
   * @param {string} fieldType - The field type (e.g., 'string', 'integer', 'date')
   * @param {boolean} aggregatable - Whether the field supports aggregation
   * @param {string|null} currentAggregate - The currently applied aggregate (if any)
   */
  updateAggregateVisibility(fieldType, aggregatable, currentAggregate) {
    if (!this.hasColumnOptionsDropdownTarget) return;

    const dropdown = this.columnOptionsDropdownTarget;
    const aggregateSection = dropdown.querySelector("[data-aggregate-section]");
    const aggregateItems = dropdown.querySelectorAll("[data-aggregate-types]");
    const hasAggregate = currentAggregate && currentAggregate.length > 0;

    // Hide entire aggregate section if field is not aggregatable AND no aggregate is currently applied
    // (If an aggregate IS applied, we must show at least "None" so user can remove it)
    if (!aggregatable && !hasAggregate) {
      if (aggregateSection) aggregateSection.classList.add("hidden");
      aggregateItems.forEach((item) => item.classList.add("hidden"));
      return;
    }

    // Show aggregate section
    if (aggregateSection) aggregateSection.classList.remove("hidden");

    // Show/hide individual aggregate options based on field type
    // Always show "None" option (data-aggregate="") if an aggregate is applied or field is aggregatable
    aggregateItems.forEach((item) => {
      const allowedTypes = item.dataset.aggregateTypes;
      const isNoneOption = item.querySelector('[data-aggregate=""]') !== null;

      if (isNoneOption && hasAggregate) {
        // Always show "None" when an aggregate is currently applied
        item.classList.remove("hidden");
      } else if (
        aggregatable &&
        (allowedTypes === "all" || allowedTypes.split(" ").includes(fieldType))
      ) {
        item.classList.remove("hidden");
      } else if (!aggregatable && hasAggregate) {
        // Field isn't aggregatable but has an aggregate - only show "None"
        item.classList.add("hidden");
      } else {
        item.classList.add("hidden");
      }
    });
  }

  /**
   * Show/hide date format menu items based on the column's field type
   * and highlight the currently selected format
   * @param {string} fieldType - The field type (e.g., 'date', 'datetime', 'string')
   * @param {string|null} currentDateFormat - The currently applied date format
   */
  updateDateFormatVisibility(fieldType, currentDateFormat) {
    if (!this.hasColumnOptionsDropdownTarget) return;

    const dropdown = this.columnOptionsDropdownTarget;
    const dateFormatSection = dropdown.querySelector(
      "[data-date-format-section]",
    );
    const dateFormatItems = dropdown.querySelectorAll(
      "[data-date-format-types]",
    );
    const isDateType = fieldType === "date" || fieldType === "datetime";

    // Hide entire section if field is not a date/datetime type
    if (!isDateType) {
      if (dateFormatSection) dateFormatSection.classList.add("hidden");
      dateFormatItems.forEach((item) => item.classList.add("hidden"));
      return;
    }

    // Show date format section
    if (dateFormatSection) dateFormatSection.classList.remove("hidden");
    dateFormatItems.forEach((item) => item.classList.remove("hidden"));

    // Update checkmark indicators
    const checks = dropdown.querySelectorAll("[data-date-format-check]");
    checks.forEach((check) => {
      const format = check.dataset.dateFormatCheck;
      const isActive =
        (format === "" && !currentDateFormat) || format === currentDateFormat;
      check.classList.toggle("hidden", !isActive);
    });
  }

  hideColumnOptions() {
    if (this.hasColumnOptionsDropdownTarget) {
      this.columnOptionsDropdownTarget.classList.add("hidden");
    }
    this.activeColumnIndex = null;
  }

  sortColumnAsc() {
    if (this.activeColumnIndex === null) return;
    const col = this.columns[this.activeColumnIndex];
    if (!col) return;

    const sort = { field_key: col.field_key, direction: "asc" };
    if (col.relationship_path) sort.relationship_path = col.relationship_path;
    this.sorts = [sort];
    this.ephemeralSort = { ...sort };
    this.syncConfigs();
    this.hideColumnOptions();
    this.refreshPreviewWithDraft();
  }

  sortColumnDesc() {
    if (this.activeColumnIndex === null) return;
    const col = this.columns[this.activeColumnIndex];
    if (!col) return;

    const sort = { field_key: col.field_key, direction: "desc" };
    if (col.relationship_path) sort.relationship_path = col.relationship_path;
    this.sorts = [sort];
    this.ephemeralSort = { ...sort };
    this.syncConfigs();
    this.hideColumnOptions();
    this.refreshPreviewWithDraft();
  }

  clearSort() {
    if (this.activeColumnIndex === null) return;
    const col = this.columns[this.activeColumnIndex];
    if (!col) return;

    // Remove any sort on this column (match both field_key and relationship_path)
    this.sorts = this.sorts.filter(
      (s) =>
        s.field_key !== col.field_key ||
        s.relationship_path !== col.relationship_path,
    );
    if (
      this.ephemeralSort &&
      this.ephemeralSort.field_key === col.field_key &&
      this.ephemeralSort.relationship_path === col.relationship_path
    ) {
      this.ephemeralSort = null;
    }
    this.syncConfigs();
    this.hideColumnOptions();
    this.refreshPreviewWithDraft();
  }

  setAggregate(event) {
    if (this.activeColumnIndex === null) return;
    const aggregate = event.currentTarget.dataset.aggregate;

    if (this.columns[this.activeColumnIndex]) {
      this.columns[this.activeColumnIndex].aggregate = aggregate || null;
      this.syncConfigs();
      this.hideColumnOptions();
      this.refreshPreviewWithDraft();
    }
  }

  setDateFormat(event) {
    if (this.activeColumnIndex === null) return;
    const dateFormat = event.currentTarget.dataset.dateFormat;

    if (this.columns[this.activeColumnIndex]) {
      this.columns[this.activeColumnIndex].date_format = dateFormat || null;
      this.syncConfigs();
      this.hideColumnOptions();
      this.refreshPreviewWithDraft();
    }
  }

  removeColumnFromMenu() {
    if (this.activeColumnIndex === null) return;

    // Don't allow removal of persistent columns
    const col = this.columns[this.activeColumnIndex];
    if (col?.persistent === true) {
      this.hideColumnOptions();
      return;
    }

    this.columns.splice(this.activeColumnIndex, 1);
    this.syncConfigs();
    this.hideColumnOptions();
    this.refreshPreviewWithDraft();
    this.syncFieldPickerState();
  }

  // ==========================================
  // Column Drag and Drop
  // ==========================================

  dragColumnStart(event) {
    const th = event.currentTarget;
    const index = parseInt(th.dataset.columnIndex);

    if (isNaN(index)) return;

    this.draggedColumnIndex = index;
    event.dataTransfer.setData("text/plain", index.toString());
    event.dataTransfer.effectAllowed = "move";

    requestAnimationFrame(() => {
      th.classList.add("opacity-50", "bg-primary/20");
    });
  }

  dragColumnOver(event) {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    const th = event.currentTarget;
    const toIndex = parseInt(th.dataset.columnIndex);
    const fromIndex = this.draggedColumnIndex;

    if (fromIndex === null || fromIndex === undefined || fromIndex === toIndex)
      return;

    this.clearDropIndicators();

    if (fromIndex < toIndex) {
      th.classList.add("border-r-4", "border-primary");
    } else {
      th.classList.add("border-l-4", "border-primary");
    }
  }

  dragColumnLeave(event) {
    const th = event.currentTarget;
    th.classList.remove("border-l-4", "border-r-4", "border-primary");
  }

  dragColumnEnd(event) {
    const th = event.currentTarget;
    th.classList.remove("opacity-50", "bg-primary/20");
    this.clearDropIndicators();
    this.draggedColumnIndex = null;
  }

  clearDropIndicators() {
    const headers = this.element.querySelectorAll("th[data-column-index]");
    headers.forEach((header) => {
      header.classList.remove("border-l-4", "border-r-4", "border-primary");
    });
  }

  dropColumn(event) {
    event.preventDefault();
    const fromIndex = parseInt(event.dataTransfer.getData("text/plain"));
    const toIndex = parseInt(event.currentTarget.dataset.columnIndex);

    this.clearDropIndicators();

    const draggedTh = this.element.querySelector(
      `th[data-column-index="${fromIndex}"]`,
    );
    if (draggedTh) {
      draggedTh.classList.remove("opacity-50", "bg-primary/20");
    }

    if (isNaN(fromIndex) || isNaN(toIndex) || fromIndex === toIndex) return;

    // Reorder columns in data
    const [removed] = this.columns.splice(fromIndex, 1);
    this.columns.splice(toIndex, 0, removed);

    this.syncConfigs();
    this.refreshPreviewWithDraft();
  }

  // ==========================================
  // Groupings (Pill/Tag Pattern)
  // ==========================================

  addGrouping(event) {
    console.log("[BaseBuilder] addGrouping() called");
    event.preventDefault();

    // Don't add if already selected
    if (event.currentTarget.hasAttribute("data-already-selected")) return;

    const fieldKey = event.currentTarget.dataset.fieldKey;
    const fieldType = event.currentTarget.dataset.fieldType;
    const fieldLabel = event.currentTarget.dataset.fieldLabel;
    const relationshipPath =
      event.currentTarget.dataset.relationshipPath || null;

    const grouping = {
      field_key: fieldKey,
      field_type: fieldType,
      time_bucket: null
    };

    // Auto-select 'month' as default time_bucket for date/datetime fields
    if (["date", "datetime"].includes(fieldType)) {
      grouping.time_bucket = "month";
    }

    if (relationshipPath) {
      grouping.relationship_path = relationshipPath;
    }

    this.groupings.push(grouping);
    this.syncConfigs();

    // Refresh the groupings frame to show the new pill
    this.refreshGroupingsFrame();

    // Refresh the preview with new grouping (sandbox mode)
    this.debouncedRefreshPreview();

    // Close dropdown by blurring
    document.activeElement.blur();
  }

  removeGrouping(event) {
    console.log("[BaseBuilder] removeGrouping() called");
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.index);

    if (isNaN(index) || index < 0 || index >= this.groupings.length) return;

    this.groupings.splice(index, 1);
    this.syncConfigs();
    this.refreshGroupingsFrame();
    this.debouncedRefreshPreview();
  }

  updateGroupingTimeBucket(event) {
    const index = parseInt(event.currentTarget.dataset.groupingIndex);
    const bucket = event.currentTarget.value || null;

    if (isNaN(index) || !this.groupings[index]) return;

    this.groupings[index].time_bucket = bucket;
    this.syncConfigs();
    this.debouncedRefreshPreview();
  }

  addGroupingBand(event) {
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.groupingIndex);

    if (isNaN(index) || !this.groupings[index]) return;

    const grouping = this.groupings[index];
    grouping.bands = grouping.bands || [];
    grouping.bands.push({ min: nextBandMin(grouping.bands, this.bandStepFor(index)), max: null });
    this.syncConfigs();
    this.refreshGroupingsFrame();
    this.debouncedRefreshPreview();
  }

  removeGroupingBand(event) {
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.groupingIndex);
    const bandIndex = parseInt(event.currentTarget.dataset.bandIndex);
    const bands = this.groupings[index]?.bands;

    if (isNaN(bandIndex) || !bands || bandIndex >= bands.length) return;

    bands.splice(bandIndex, 1);
    this.syncConfigs();
    this.refreshGroupingsFrame();
    this.debouncedRefreshPreview();
  }

  // Fires when the value is committed (blur, tab-away, or spinner click):
  // the new value, neighbor clamping, warnings, and preview all apply
  // together. Clamping waits for commit because typing "18" passes through
  // "1", and rewriting other rows against a half-typed number has no undo.
  updateGroupingBand(event) {
    const index = parseInt(event.currentTarget.dataset.groupingIndex);
    const bandIndex = parseInt(event.currentTarget.dataset.bandIndex);
    const bound = event.currentTarget.dataset.bandBound;
    const bands = this.groupings[index]?.bands;
    const band = bands?.[bandIndex];

    if (!band || !["min", "max"].includes(bound)) return;

    const raw = event.currentTarget.value;
    band[bound] = raw === "" ? null : Number(raw);

    // Clamp neighboring bands out of the edited range where possible; update
    // their inputs in place rather than re-rendering the frame, so focus
    // survives when the user tabs between fields
    resolveOverlaps(bands, bandIndex, this.bandStepFor(index)).forEach((adjustment) => {
      bands[adjustment.bandIndex][adjustment.bound] = adjustment.value;
      const input = this.element.querySelector(
        `[data-grouping-index="${index}"][data-band-index="${adjustment.bandIndex}"][data-band-bound="${adjustment.bound}"]`,
      );
      if (input) input.value = adjustment.value;
    });

    this.syncConfigs();
    this.renderBandWarnings();
    this.debouncedRefreshPreview();
  }

  // Granularity of the grouping's field, rendered onto the pill by the
  // groupings frame. Whole-number fields step by 1; continuous ones step by 0
  // so ranges touch instead of leaving an uncovered interval between them.
  bandStepFor(index) {
    const pill = this.element.querySelector(`[data-grouping-index="${index}"][data-band-step]`);
    return pill?.dataset.bandStep === "0" ? 0 : 1;
  }

  // Warn when configured bands leave interior gaps (rows silently drop from
  // the chart) or overlap in a way resolveOverlaps couldn't fix. Templates
  // come from data attributes so the strings stay in the locale files.
  renderBandWarnings() {
    this.element.querySelectorAll("[data-band-warnings]").forEach((wrapper) => {
      const pill = wrapper.closest("[data-grouping-index]");
      const index = parseInt(pill?.dataset.groupingIndex);
      const messages = bandWarnings(this.groupings[index]?.bands, this.bandStepFor(index)).map((warning) =>
        warning.type === "overlap"
          ? wrapper.dataset.warningOverlap
              .replace("%{a}", warning.a)
              .replace("%{b}", warning.b)
          : wrapper.dataset.warningGap.replace("%{range}", warning.range),
      );

      const text = wrapper.querySelector("[data-band-warnings-text]");
      if (text) text.textContent = messages.join(" · ");
      wrapper.classList.toggle("hidden", messages.length === 0);
      wrapper.classList.toggle("flex", messages.length > 0);
    });
  }

  refreshGroupingsFrame() {
    console.log("[BaseBuilder] refreshGroupingsFrame() called");
    const entityId = this.entityId;
    if (!entityId || !this.hasGroupingsFrameTarget) return;

    // Build URL with draft_config for sandbox mode (no persistence)
    const url = new URL(this.groupingsFrameUrl, window.location.origin);
    url.searchParams.set(
      "draft_config",
      JSON.stringify(this.buildDraftConfig()),
    );

    // Fetch the response which contains both turbo-frames
    fetch(url.toString(), {
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
      .then((response) => response.text())
      .then((html) => {
        // Parse the response and update both frames
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        // Update the groupings pills frame
        const newGroupingsFrame = doc.querySelector(
          "turbo-frame#groupings_frame",
        );
        const currentGroupingsFrame = this.groupingsFrameTarget.querySelector(
          "turbo-frame#groupings_frame",
        );
        if (newGroupingsFrame && currentGroupingsFrame) {
          currentGroupingsFrame.innerHTML = newGroupingsFrame.innerHTML;
        }

        // Update the picker dropdown frame
        const newPickerFrame = doc.querySelector(
          "turbo-frame#groupings_picker_frame",
        );
        const currentPickerFrame = this.hasGroupingPickerDropdownTarget
          ? this.groupingPickerDropdownTarget.querySelector(
              "turbo-frame#groupings_picker_frame",
            )
          : null;
        if (newPickerFrame && currentPickerFrame) {
          currentPickerFrame.innerHTML = newPickerFrame.innerHTML;
        }

        // Re-run after the frame swap replaces the warning containers
        this.renderBandWarnings();
      })
      .catch((error) => console.error("Error refreshing groupings:", error));
  }

  // Legacy methods for backward compatibility (checkbox-based grouping)
  // Can be removed once all views use the new pill pattern
  toggleGrouping(event) {
    const fieldKey = event.currentTarget.dataset.fieldKey;
    const fieldType = event.currentTarget.dataset.fieldType;
    const relationshipPath =
      event.currentTarget.dataset.relationshipPath || null;
    const isChecked = event.currentTarget.checked;

    if (isChecked) {
      const grouping = {
        field_key: fieldKey,
        field_type: fieldType,
        time_bucket: null
      };
      if (relationshipPath) {
        grouping.relationship_path = relationshipPath;
      }
      this.groupings.push(grouping);
    } else {
      this.groupings = this.groupings.filter(
        (g) =>
          !(
            g.field_key === fieldKey &&
            (g.relationship_path || null) === relationshipPath
          ),
      );
    }

    this.syncConfigs();
    this.refreshPreviewWithDraft();
  }

  // ==========================================
  // Filters (Pill/Popover Pattern)
  // ==========================================

  // Legacy method - adds empty filter (for backward compatibility)
  addFilter() {
    this.filters.push({
      field_key: "",
      operator: "eq",
      value: "",
      enabled: true,
      relationship_path: null
    });
    this.syncConfigs();
    this.refreshFiltersFrame();
  }

  // New method - adds filter with field pre-selected from picker
  addFilterFromPicker(event) {
    console.log("[BaseBuilder] addFilterFromPicker() called");
    event.preventDefault();

    const fieldKey = event.currentTarget.dataset.fieldKey;
    const fieldType = event.currentTarget.dataset.fieldType;
    const fieldLabel = event.currentTarget.dataset.fieldLabel;
    const relationshipPath =
      event.currentTarget.dataset.relationshipPath || null;

    const filter = {
      field_key: fieldKey,
      field_type: fieldType,
      operator: this.defaultOperatorForFieldType(fieldType),
      value: "",
      enabled: true,
      relationship_path: relationshipPath
    };

    this.filters.push(filter);
    this.syncConfigs();

    // Refresh the filters frame to show the new filter
    this.refreshFiltersFrame();

    // Close dropdown by blurring
    document.activeElement.blur();
  }

  removeFilter(event) {
    console.log("[BaseBuilder] removeFilter() called");
    const index = parseInt(event.currentTarget.dataset.index);
    if (isNaN(index) || index < 0 || index >= this.filters.length) return;

    this.filters.splice(index, 1);
    this.syncConfigs();
    this.refreshFiltersFrame();
    this.debouncedRefreshPreview();
  }

  toggleGroupHistory(event) {
    const { filterIndex, nestedIndex } = event.currentTarget.dataset
    const parent = this.filters[Number(filterIndex)]
    const filter = nestedIndex ? parent?.filters?.[Number(nestedIndex)] : parent
    if (!filter) return
    filter.use_membership_history = event.currentTarget.checked
    this.syncConfigs()
    this.refreshFiltersFrame()
    this.debouncedRefreshPreview()
  }

  async showGroupHistoryMembers(event) {
    const expander = event.currentTarget
    if (!expander.open) return
    const result = expander.querySelector("[data-group-history-result]")
    result.textContent = result.dataset.loading
    const url = new URL(this.groupHistoryMembersUrl, window.location.origin)
    url.searchParams.set("draft_config", JSON.stringify(this.buildDraftConfig()))
    url.searchParams.set("filter_index", expander.dataset.filterIndex)
    if (expander.dataset.nestedIndex) url.searchParams.set("nested_index", expander.dataset.nestedIndex)
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error("Member preview failed")
      const data = await response.json()
      result.replaceChildren()
      if (!data.names.length) result.textContent = result.dataset.empty
      const list = document.createElement("ul")
      list.className = "list-disc pl-5"
      data.names.forEach(name => {
        const item = document.createElement("li")
        item.textContent = name
        list.appendChild(item)
      })
      result.appendChild(list)
      if (data.truncated) result.append(document.createTextNode(result.dataset.truncated))
    } catch {
      result.textContent = result.dataset.error
    }
  }

  updateFilter(event) {
    const index = parseInt(event.currentTarget.dataset.filterIndex);
    const field = event.currentTarget.dataset.filterField;
    const value = event.currentTarget.value;

    if (isNaN(index) || !this.filters[index]) return;

    const previousOperator = this.filters[index].operator;
    this.filters[index][field] = value;

    // Clear value when field changes (different type may have different input)
    if (field === "field_key") {
      this.filters[index].value = "";
      this.filters[index].operator = "eq"; // Reset operator to default

      // Capture relationship_path from the selected option's data-relationship attribute
      const selectedOption = event.currentTarget.selectedOptions[0];
      const relationshipPath = selectedOption?.dataset?.relationship || null;
      this.filters[index].relationship_path = relationshipPath;

      this.refreshFiltersFrame();
    }

    // When operator changes, may need to refresh for different value input type
    if (field === "operator") {
      this.filters[index].value = this.valueForOperatorChange(
        this.filters[index].value,
        previousOperator,
        value,
      );
      this.refreshFiltersFrame();

      // Refresh preview if operator doesn't need a value OR if value is already present
      const filterValue = this.filters[index].value;
      const hasValue = Array.isArray(filterValue)
        ? filterValue.some((v) => v !== "" && v !== null)
        : filterValue !== "" &&
          filterValue !== null &&
          filterValue !== undefined;

      if (NO_VALUE_OPERATORS.includes(value) || hasValue) {
        this.debouncedRefreshPreview();
      }
    }

    this.syncConfigs();

    // Only refresh preview on value change, not field/operator change
    if (field === "value") {
      this.debouncedRefreshPreview();
    }
  }

  /**
   * Value a filter keeps after its operator changes. Operators come in value
   * shapes — a range for between, a day count for the N-days pair, nothing for
   * the no-value operators, a scalar otherwise — and a value only survives a
   * change within its shape. Question-response filters keep their question_id
   * regardless, so the picked question is never lost.
   */
  valueForOperatorChange(currentValue, previousOperator, nextOperator) {
    if (this.valueShapeFor(previousOperator) === this.valueShapeFor(nextOperator)) {
      return currentValue;
    }
    if (nextOperator === "between") return ["", ""];
    return this.clearedValue(currentValue);
  }

  valueShapeFor(operator) {
    if (operator === "between") return "range";
    if (operator === "last_n_days" || operator === "older_than_n_days") return "days";
    if (NO_VALUE_OPERATORS.includes(operator)) return "none";
    return "scalar";
  }

  clearedValue(currentValue) {
    const questionId = !Array.isArray(currentValue) && currentValue?.question_id;
    return questionId ? { question_id: questionId } : "";
  }

  updateBetweenFilter(event) {
    const index = parseInt(event.currentTarget.dataset.filterIndex);
    const field = event.currentTarget.dataset.filterField; // 'value_start' or 'value_end'
    const value = event.currentTarget.value;

    if (isNaN(index) || !this.filters[index]) return;

    // Ensure value is an array for "between" operator
    if (!Array.isArray(this.filters[index].value)) {
      this.filters[index].value = ["", ""];
    }

    if (field === "value_start") {
      this.filters[index].value[0] = value;
    } else if (field === "value_end") {
      this.filters[index].value[1] = value;
    }

    this.syncConfigs();
    this.debouncedRefreshPreview();
  }

  updateForeignKeyFilter(event) {
    // Handle tom-select:change event for foreign key filters
    // The event is dispatched on the wrapper div (which has data-controller="tom-select")
    // and event.currentTarget is the element with the data-action (the wrapper div)
    const wrapper = event.currentTarget;
    const index = parseInt(wrapper.dataset.filterIndex);
    const value = event.detail?.value;

    if (isNaN(index) || !this.filters[index]) return;

    this.filters[index].value = value;

    // Store the label for display when the filter is re-rendered
    const tomSelect = event.detail?.tomSelect;
    if (tomSelect && value) {
      const option = tomSelect.options[value];
      if (option) {
        this.filters[index].value_label = option.text || option.label || value;
      }
    }

    this.syncConfigs();
    this.debouncedRefreshPreview();
  }

  // ==========================================
  // Question Response Filter Methods
  // ==========================================

  /**
   * Handle question selection for question_response filters
   * Updates the filter with question_id and metadata (question_type, question_title)
   * Then refreshes the filters frame to show the appropriate value input
   */
  updateQuestionFilter(event) {
    const wrapper = event.currentTarget;
    const index = parseInt(wrapper.dataset.filterIndex);
    const questionId = event.detail?.value;
    const tomSelect = event.detail?.tomSelect;

    if (isNaN(index) || !this.filters[index]) return;

    // Get question metadata from tom-select option (includes extra_attributes)
    let questionType = null;
    let questionTitle = null;
    if (tomSelect && questionId) {
      const option = tomSelect.options[questionId];
      if (option) {
        questionType = option.question_type; // From extra_attributes in LookupService
        questionTitle = option.text;
      }
    }

    // Update filter with question info
    // For question_response, value is an object with question_id and response_value/option_ids
    this.filters[index].value = {
      question_id: questionId ? parseInt(questionId) : null
    };
    this.filters[index].metadata = {
      question_type: questionType,
      question_title: questionTitle
    };
    // Set default operator based on question type
    this.filters[index].operator =
      this.getDefaultOperatorForQuestionType(questionType);

    this.syncConfigs();
    // Refresh filters frame to show the new value input based on question type
    this.refreshFiltersFrame();
  }

  /**
   * Get the default operator for a given question type
   */
  // A string_array field filters by set membership only ("is one of"), so a new
  // filter must start on a valid operator — "eq" isn't offered and would leave
  // the value widget unrendered. Every other type keeps the "eq" default.
  defaultOperatorForFieldType(fieldType) {
    return fieldType === "string_array" ? "in" : "eq";
  }

  getDefaultOperatorForQuestionType(questionType) {
    switch (questionType) {
      case "radio":
      case "checkbox":
      case "select":
        return "in";
      case "boolean":
        return "eq";
      case "number":
      case "rating_scale":
        return "eq";
      case "date":
      case "datetime":
        return "eq";
      default:
        // Text-based: short_answer, long_answer, email, phone
        return "eq";
    }
  }

  /**
   * Handle simple value input for question_response filters
   * Used for text, boolean, number, and date question types
   */
  updateQuestionValueFilter(event) {
    const input = event.currentTarget;
    const index = parseInt(input.dataset.filterIndex);
    const value = input.value;

    if (isNaN(index) || !this.filters[index]) return;

    // Ensure value is an object structure
    if (
      typeof this.filters[index].value !== "object" ||
      this.filters[index].value === null
    ) {
      this.filters[index].value = {};
    }

    this.filters[index].value.response_value = value;

    this.syncConfigs();
    this.debouncedRefreshPreview();
  }

  /**
   * Handle multiple choice option selection for question_response filters
   * Used for radio, checkbox, and select question types
   */
  updateQuestionOptionsFilter(event) {
    const wrapper = event.currentTarget;
    const index = parseInt(wrapper.dataset.filterIndex);
    const tomSelect = event.detail?.tomSelect;

    if (isNaN(index) || !this.filters[index]) return;

    // Get all selected option IDs
    const optionIds = tomSelect ? tomSelect.getValue() : [];

    // Ensure value is an object structure
    if (
      typeof this.filters[index].value !== "object" ||
      this.filters[index].value === null
    ) {
      this.filters[index].value = {};
    }

    // Store as array of integers
    this.filters[index].value.option_ids = optionIds.map((id) => parseInt(id));

    this.syncConfigs();
    this.debouncedRefreshPreview();
  }

  // ==========================================
  // OR Filter Groups
  // ==========================================

  /**
   * Add an empty OR group filter
   */
  addOrGroup(event) {
    event.preventDefault();
    console.log("[BaseBuilder] addOrGroup() called");

    const group = {
      type: "group",
      logic: "or",
      filters: [],
      enabled: true
    };

    this.filters.push(group);
    this.syncConfigs();
    this.refreshFiltersFrame();

    // Close dropdown by blurring
    document.activeElement.blur();
  }

  /**
   * Add a filter to an existing OR group
   * Opens a mini field picker or prompts for field selection
   */
  addFilterToGroup(event) {
    event.preventDefault();
    const groupIndex = parseInt(event.currentTarget.dataset.groupIndex);

    if (
      isNaN(groupIndex) ||
      !this.filters[groupIndex] ||
      this.filters[groupIndex].type !== "group"
    ) {
      return;
    }

    // For now, add a placeholder filter that the user can configure
    // The UI will show a field selector
    const nestedFilter = {
      field_key: "",
      operator: "eq",
      value: "",
      enabled: true
    };

    this.filters[groupIndex].filters = this.filters[groupIndex].filters || [];
    this.filters[groupIndex].filters.push(nestedFilter);
    this.syncConfigs();
    this.refreshFiltersFrame();
  }

  /**
   * Add filter to group from the nested picker (with field already selected)
   */
  addFilterToGroupFromPicker(event) {
    event.preventDefault();
    const groupIndex = parseInt(event.currentTarget.dataset.groupIndex);
    const fieldKey = event.currentTarget.dataset.fieldKey;
    const fieldType = event.currentTarget.dataset.fieldType;
    const relationshipPath =
      event.currentTarget.dataset.relationshipPath || null;

    if (
      isNaN(groupIndex) ||
      !this.filters[groupIndex] ||
      this.filters[groupIndex].type !== "group"
    ) {
      return;
    }

    const nestedFilter = {
      field_key: fieldKey,
      field_type: fieldType,
      operator: this.defaultOperatorForFieldType(fieldType),
      value: "",
      enabled: true,
      relationship_path: relationshipPath
    };

    this.filters[groupIndex].filters = this.filters[groupIndex].filters || [];
    this.filters[groupIndex].filters.push(nestedFilter);
    this.syncConfigs();
    this.refreshFiltersFrame();

    // Close any dropdown
    document.activeElement.blur();
  }

  /**
   * Remove a filter from an OR group
   */
  removeFilterFromGroup(event) {
    event.preventDefault();
    const groupIndex = parseInt(event.currentTarget.dataset.groupIndex);
    const nestedIndex = parseInt(event.currentTarget.dataset.nestedFilterIndex);

    if (isNaN(groupIndex) || isNaN(nestedIndex)) return;
    if (!this.filters[groupIndex] || this.filters[groupIndex].type !== "group")
      return;

    const nestedFilters = this.filters[groupIndex].filters || [];
    if (nestedIndex < 0 || nestedIndex >= nestedFilters.length) return;

    this.filters[groupIndex].filters.splice(nestedIndex, 1);
    this.syncConfigs();
    this.refreshFiltersFrame();
    this.debouncedRefreshPreview();
  }

  /**
   * Remove an entire OR group
   */
  removeFilterGroup(event) {
    event.preventDefault();
    const groupIndex = parseInt(event.currentTarget.dataset.groupIndex);

    if (
      isNaN(groupIndex) ||
      groupIndex < 0 ||
      groupIndex >= this.filters.length
    )
      return;
    if (this.filters[groupIndex].type !== "group") return;

    this.filters.splice(groupIndex, 1);
    this.syncConfigs();
    this.refreshFiltersFrame();
    this.debouncedRefreshPreview();
  }

  /**
   * Update a filter within an OR group
   */
  updateGroupFilter(event) {
    const groupIndex = parseInt(event.currentTarget.dataset.groupIndex);
    const nestedIndex = parseInt(event.currentTarget.dataset.nestedFilterIndex);
    const field = event.currentTarget.dataset.filterField;
    const value = event.currentTarget.value;

    if (isNaN(groupIndex) || isNaN(nestedIndex)) return;
    if (!this.filters[groupIndex] || this.filters[groupIndex].type !== "group")
      return;

    const nestedFilters = this.filters[groupIndex].filters || [];
    if (nestedIndex < 0 || nestedIndex >= nestedFilters.length) return;

    const nested = this.filters[groupIndex].filters[nestedIndex];
    const previousOperator = nested.operator;
    nested[field] = value;

    if (field === "operator") {
      nested.value = this.valueForOperatorChange(nested.value, previousOperator, value);
      this.refreshFiltersFrame();
    }

    this.syncConfigs();

    // Refresh preview on value change
    if (field === "value") {
      this.debouncedRefreshPreview();
    }
  }

  /**
   * Update a between filter within an OR group
   */
  updateGroupBetweenFilter(event) {
    const groupIndex = parseInt(event.currentTarget.dataset.groupIndex);
    const nestedIndex = parseInt(event.currentTarget.dataset.nestedFilterIndex);
    const field = event.currentTarget.dataset.filterField; // 'value_start' or 'value_end'
    const value = event.currentTarget.value;

    if (isNaN(groupIndex) || isNaN(nestedIndex)) return;
    if (!this.filters[groupIndex] || this.filters[groupIndex].type !== "group")
      return;

    const nestedFilters = this.filters[groupIndex].filters || [];
    if (nestedIndex < 0 || nestedIndex >= nestedFilters.length) return;

    // Ensure value is an array for "between" operator
    if (!Array.isArray(this.filters[groupIndex].filters[nestedIndex].value)) {
      this.filters[groupIndex].filters[nestedIndex].value = ["", ""];
    }

    if (field === "value_start") {
      this.filters[groupIndex].filters[nestedIndex].value[0] = value;
    } else if (field === "value_end") {
      this.filters[groupIndex].filters[nestedIndex].value[1] = value;
    }

    this.syncConfigs();
    this.debouncedRefreshPreview();
  }

  /**
   * Update a foreign key filter within an OR group
   */
  updateGroupForeignKeyFilter(event) {
    const wrapper = event.currentTarget;
    const groupIndex = parseInt(wrapper.dataset.groupIndex);
    const nestedIndex = parseInt(wrapper.dataset.nestedFilterIndex);
    const value = event.detail?.value;

    if (isNaN(groupIndex) || isNaN(nestedIndex)) return;
    if (!this.filters[groupIndex] || this.filters[groupIndex].type !== "group")
      return;

    const nestedFilters = this.filters[groupIndex].filters || [];
    if (nestedIndex < 0 || nestedIndex >= nestedFilters.length) return;

    this.filters[groupIndex].filters[nestedIndex].value = value;

    // Store the label for display when the filter is re-rendered
    const tomSelect = event.detail?.tomSelect;
    if (tomSelect && value) {
      const option = tomSelect.options[value];
      if (option) {
        this.filters[groupIndex].filters[nestedIndex].value_label =
          option.text || option.label || value;
      }
    }

    this.syncConfigs();
    this.debouncedRefreshPreview();
  }

  /**
   * Handle multi-value filter for enum fields with in/not_in operator
   */
  updateMultiValueFilter(event) {
    const wrapper = event.currentTarget;
    const filterIndex = parseInt(wrapper.dataset.filterIndex);
    const tomSelect = event.detail?.tomSelect;

    if (isNaN(filterIndex) || !this.filters[filterIndex]) return;

    // Get all selected values as array
    const values = tomSelect ? tomSelect.getValue() : [];

    this.filters[filterIndex].value = values;
    this.syncConfigs();
    this.debouncedRefreshPreview();
  }

  /**
   * Handle multi-value filter for enum fields within an OR group
   */
  updateGroupMultiValueFilter(event) {
    const wrapper = event.currentTarget;
    const groupIndex = parseInt(wrapper.dataset.groupIndex);
    const nestedIndex = parseInt(wrapper.dataset.nestedFilterIndex);
    const tomSelect = event.detail?.tomSelect;

    if (isNaN(groupIndex) || isNaN(nestedIndex)) return;
    if (!this.filters[groupIndex] || this.filters[groupIndex].type !== "group")
      return;

    const nestedFilters = this.filters[groupIndex].filters || [];
    if (nestedIndex < 0 || nestedIndex >= nestedFilters.length) return;

    // Get all selected values as array
    const values = tomSelect ? tomSelect.getValue() : [];

    this.filters[groupIndex].filters[nestedIndex].value = values;
    this.syncConfigs();
    this.debouncedRefreshPreview();
  }

  /**
   * Handle multi-value foreign key filter (in/not_in operator)
   */
  updateMultiForeignKeyFilter(event) {
    const wrapper = event.currentTarget;
    const filterIndex = parseInt(wrapper.dataset.filterIndex);
    const tomSelect = event.detail?.tomSelect;

    if (isNaN(filterIndex) || !this.filters[filterIndex]) return;

    // Get all selected values and labels
    const values = tomSelect ? tomSelect.getValue() : [];
    const labels = values.map((v) => {
      const option = tomSelect?.options[v];
      return option ? option.text || option.label || v : v;
    });

    this.filters[filterIndex].value = values;
    this.filters[filterIndex].value_labels = labels;
    this.syncConfigs();
    this.debouncedRefreshPreview();
  }

  /**
   * Handle multi-value foreign key filter within an OR group
   */
  updateGroupMultiForeignKeyFilter(event) {
    const wrapper = event.currentTarget;
    const groupIndex = parseInt(wrapper.dataset.groupIndex);
    const nestedIndex = parseInt(wrapper.dataset.nestedFilterIndex);
    const tomSelect = event.detail?.tomSelect;

    if (isNaN(groupIndex) || isNaN(nestedIndex)) return;
    if (!this.filters[groupIndex] || this.filters[groupIndex].type !== "group")
      return;

    const nestedFilters = this.filters[groupIndex].filters || [];
    if (nestedIndex < 0 || nestedIndex >= nestedFilters.length) return;

    // Get all selected values and labels
    const values = tomSelect ? tomSelect.getValue() : [];
    const labels = values.map((v) => {
      const option = tomSelect?.options[v];
      return option ? option.text || option.label || v : v;
    });

    this.filters[groupIndex].filters[nestedIndex].value = values;
    this.filters[groupIndex].filters[nestedIndex].value_labels = labels;
    this.syncConfigs();
    this.debouncedRefreshPreview();
  }

  /**
   * Handle question selection for question_response filters within an OR group
   */
  updateGroupQuestionFilter(event) {
    const wrapper = event.currentTarget;
    const groupIndex = parseInt(wrapper.dataset.groupIndex);
    const nestedIndex = parseInt(wrapper.dataset.nestedFilterIndex);
    const questionId = event.detail?.value;
    const tomSelect = event.detail?.tomSelect;

    if (isNaN(groupIndex) || isNaN(nestedIndex)) return;
    if (!this.filters[groupIndex] || this.filters[groupIndex].type !== "group")
      return;

    const nestedFilters = this.filters[groupIndex].filters || [];
    if (nestedIndex < 0 || nestedIndex >= nestedFilters.length) return;

    // Get question metadata from tom-select option
    let questionType = null;
    let questionTitle = null;
    if (tomSelect && questionId) {
      const option = tomSelect.options[questionId];
      if (option) {
        questionType = option.question_type;
        questionTitle = option.text;
      }
    }

    // Update filter with question info
    this.filters[groupIndex].filters[nestedIndex].value = {
      question_id: questionId ? parseInt(questionId) : null
    };
    this.filters[groupIndex].filters[nestedIndex].metadata = {
      question_type: questionType,
      question_title: questionTitle
    };
    this.filters[groupIndex].filters[nestedIndex].operator =
      this.getDefaultOperatorForQuestionType(questionType);

    this.syncConfigs();
    this.refreshFiltersFrame();
  }

  /**
   * Handle simple value input for question_response filters within an OR group
   */
  updateGroupQuestionValueFilter(event) {
    const input = event.currentTarget;
    const groupIndex = parseInt(input.dataset.groupIndex);
    const nestedIndex = parseInt(input.dataset.nestedFilterIndex);
    const value = input.value;

    if (isNaN(groupIndex) || isNaN(nestedIndex)) return;
    if (!this.filters[groupIndex] || this.filters[groupIndex].type !== "group")
      return;

    const nestedFilters = this.filters[groupIndex].filters || [];
    if (nestedIndex < 0 || nestedIndex >= nestedFilters.length) return;

    // Ensure value is an object structure
    if (
      typeof this.filters[groupIndex].filters[nestedIndex].value !== "object" ||
      this.filters[groupIndex].filters[nestedIndex].value === null
    ) {
      this.filters[groupIndex].filters[nestedIndex].value = {};
    }

    this.filters[groupIndex].filters[nestedIndex].value.response_value = value;
    this.syncConfigs();
    this.debouncedRefreshPreview();
  }

  /**
   * Handle multiple choice option selection for question_response filters within an OR group
   */
  updateGroupQuestionOptionsFilter(event) {
    const wrapper = event.currentTarget;
    const groupIndex = parseInt(wrapper.dataset.groupIndex);
    const nestedIndex = parseInt(wrapper.dataset.nestedFilterIndex);
    const tomSelect = event.detail?.tomSelect;

    if (isNaN(groupIndex) || isNaN(nestedIndex)) return;
    if (!this.filters[groupIndex] || this.filters[groupIndex].type !== "group")
      return;

    const nestedFilters = this.filters[groupIndex].filters || [];
    if (nestedIndex < 0 || nestedIndex >= nestedFilters.length) return;

    // Get all selected option IDs
    const optionIds = tomSelect ? tomSelect.getValue() : [];

    // Ensure value is an object structure
    if (
      typeof this.filters[groupIndex].filters[nestedIndex].value !== "object" ||
      this.filters[groupIndex].filters[nestedIndex].value === null
    ) {
      this.filters[groupIndex].filters[nestedIndex].value = {};
    }

    // Store as array of integers
    this.filters[groupIndex].filters[nestedIndex].value.option_ids =
      optionIds.map((id) => parseInt(id));
    this.syncConfigs();
    this.debouncedRefreshPreview();
  }

  // ==========================================
  // Cascading Filter Methods
  // ==========================================

  /**
   * Handle cascade filter changes (top-level filter)
   * Triggered by cascading-filter controller's cascadeChanged event
   *
   * Cascade values use the field keys from cascade_fields config.
   * For survey_response filter with cascade_fields: ['survey_id', 'question_id']:
   * - survey_id: selected survey ID
   * - survey_id_label: survey display name
   * - question_id: selected question ID
   * - question_id_label: question title
   * - question_id_question_type: question type (from extra_attributes)
   */
  updateCascadeFilter(event) {
    const index = parseInt(event.detail?.index);
    const cascadeValues = event.detail?.values || {};

    if (isNaN(index) || !this.filters[index]) return;

    // Store cascade values in the filter
    this.filters[index].cascade_values = cascadeValues;

    // Extract question_id and type for filter execution
    // The filter applier expects value.question_id and metadata.question_type
    // Keys are based on field names: question_id, question_id_question_type, etc.
    const questionId = cascadeValues["question_id"];
    const questionType = cascadeValues["question_id_question_type"];
    const questionTitle = cascadeValues["question_id_label"];

    if (questionId) {
      // Initialize or update value object
      if (
        typeof this.filters[index].value !== "object" ||
        this.filters[index].value === null
      ) {
        this.filters[index].value = {};
      }
      this.filters[index].value.question_id = parseInt(questionId);

      // Update metadata
      this.filters[index].metadata = {
        question_type: questionType,
        question_title: questionTitle
      };

      // Set default operator based on question type
      this.filters[index].operator =
        this.getDefaultOperatorForQuestionType(questionType);
    }

    this.syncConfigs();
    // Refresh filters frame to show the new value input based on question type
    this.refreshFiltersFrame();
  }

  /**
   * Handle cascade filter changes within an OR group (nested filter)
   * Triggered by cascading-filter controller's cascadeChanged event
   */
  updateGroupCascadeFilter(event) {
    const groupIndex = parseInt(event.detail?.index);
    const nestedIndex = parseInt(event.detail?.nestedIndex);
    const cascadeValues = event.detail?.values || {};

    if (isNaN(groupIndex) || isNaN(nestedIndex)) return;
    if (!this.filters[groupIndex] || this.filters[groupIndex].type !== "group")
      return;

    const nestedFilters = this.filters[groupIndex].filters || [];
    if (nestedIndex < 0 || nestedIndex >= nestedFilters.length) return;

    // Store cascade values in the nested filter
    this.filters[groupIndex].filters[nestedIndex].cascade_values =
      cascadeValues;

    // Extract question_id and type for filter execution
    // Keys are based on field names from cascade_fields config
    const questionId = cascadeValues["question_id"];
    const questionType = cascadeValues["question_id_question_type"];
    const questionTitle = cascadeValues["question_id_label"];

    if (questionId) {
      // Initialize or update value object
      if (
        typeof this.filters[groupIndex].filters[nestedIndex].value !==
          "object" ||
        this.filters[groupIndex].filters[nestedIndex].value === null
      ) {
        this.filters[groupIndex].filters[nestedIndex].value = {};
      }
      this.filters[groupIndex].filters[nestedIndex].value.question_id =
        parseInt(questionId);

      // Update metadata
      this.filters[groupIndex].filters[nestedIndex].metadata = {
        question_type: questionType,
        question_title: questionTitle
      };

      // Set default operator
      this.filters[groupIndex].filters[nestedIndex].operator =
        this.getDefaultOperatorForQuestionType(questionType);
    }

    this.syncConfigs();
    this.refreshFiltersFrame();
  }

  refreshFiltersFrame() {
    console.log("[BaseBuilder] refreshFiltersFrame() called");
    const entityId = this.entityId;
    if (!entityId || !this.hasFiltersFrameTarget) return;

    // Build URL with draft_config for sandbox mode (no persistence)
    const url = new URL(this.filtersFrameUrl, window.location.origin);
    url.searchParams.set(
      "draft_config",
      JSON.stringify(this.buildDraftConfig()),
    );

    // Fetch the response which contains both turbo-frames
    fetch(url.toString(), {
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
      .then((response) => response.text())
      .then((html) => {
        // Parse the response and update both frames
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        // Update the filters list frame
        const newFiltersFrame = doc.querySelector("turbo-frame#filters_frame");
        const currentFiltersFrame = this.filtersFrameTarget.querySelector(
          "turbo-frame#filters_frame",
        );
        if (newFiltersFrame && currentFiltersFrame) {
          currentFiltersFrame.innerHTML = newFiltersFrame.innerHTML;
        }

        // Update the picker dropdown frame (if it exists)
        const newPickerFrame = doc.querySelector(
          "turbo-frame#filters_picker_frame",
        );
        const currentPickerFrame = this.hasFilterPickerDropdownTarget
          ? this.filterPickerDropdownTarget.querySelector(
              "turbo-frame#filters_picker_frame",
            )
          : null;
        if (newPickerFrame && currentPickerFrame) {
          currentPickerFrame.innerHTML = newPickerFrame.innerHTML;
        }
      })
      .catch((error) => console.error("Error refreshing filters:", error));
  }

  // ==========================================
  // Config Sync & Save
  // ==========================================

  syncConfigs() {
    this.markDirty();
    if (this.hasColumnsConfigTarget) {
      this.columnsConfigTarget.value = JSON.stringify(this.columns);
    }
    if (this.hasFiltersConfigTarget) {
      this.filtersConfigTarget.value = JSON.stringify(this.filters);
    }
    if (this.hasGroupingsConfigTarget) {
      this.groupingsConfigTarget.value = JSON.stringify(this.groupings);
    }
    if (this.hasSortsConfigTarget) {
      this.sortsConfigTarget.value = JSON.stringify(this.sorts);
    }
    if (this.hasVisualizationConfigTarget) {
      this.visualizationConfigTarget.value = JSON.stringify(this.visualization);
    }
    if (this.hasParametersFieldTarget) {
      this.parametersFieldTarget.value = JSON.stringify(this.parameters || {});
    }

    // Call subclass-specific sync
    this.syncEntitySpecificConfigs();
  }

  save() {
    console.log("[BaseBuilder] save() called");
    this.syncConfigs();
    if (this.hasFormTarget) {
      // Listen for successful submission to update button state
      this.formTarget.addEventListener(
        "turbo:submit-end",
        (event) => {
          if (event.detail.success) {
            this.hasUnsavedChanges = false;
            this.updateSaveButtonState();
          }
        },
        { once: true },
      );

      // Use native form submission - Turbo handles the rest
      // turbo:submit-end event fires, modal controller closes modal automatically on success
      // On validation errors, controller re-renders builder view keeping modal open
      this.formTarget.requestSubmit();
    }
  }

  showSaveSuccess() {
    const saveButton = this.element.querySelector('[data-action*="save"]');
    if (saveButton) {
      const originalText = saveButton.innerHTML;
      saveButton.innerHTML = `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg> ${i18n.t('base_builder.saved')}`;
      saveButton.classList.add("btn-success");
      saveButton.classList.remove("btn-primary", "btn-warning");

      setTimeout(() => {
        saveButton.innerHTML = originalText;
        saveButton.classList.remove("btn-success");
        saveButton.classList.add("btn-primary");
      }, 2000);
    }
  }

  showSaveError(errors) {
    // Show error toast or alert
    const message = errors?.join(", ") || i18n.t('base_builder.failed_to_save');
    console.error("Save error:", message);
    // Could add a toast notification here if we have a toast system
  }

  saveConfigs() {
    console.log("[BaseBuilder] saveConfigs() called", new Error().stack);
    this.syncConfigs();
    const formData = new FormData(this.formTarget);

    return fetch(this.formTarget.action, {
      method: "PATCH",
      body: formData,
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          ?.content
      }
    });
  }

  /**
   * DEPRECATED: Use refreshPreviewWithDraft() for sandbox mode
   * This method persists changes immediately - only use when persistence is needed
   */
  saveAndRefresh() {
    console.log("[BaseBuilder] saveAndRefresh() called", new Error().stack);
    if (!this.entityId) return;

    this.showPreviewLoading();

    this.saveConfigs()
      .then((response) => {
        if (response.ok) {
          this.hasUnsavedChanges = false;
          this.updateSaveButtonState();
          this.refreshPreview();
        } else {
          this.hidePreviewLoading();
        }
      })
      .catch(() => {
        this.hidePreviewLoading();
      });
  }

  /**
   * DEPRECATED: Use debouncedRefreshPreview() for sandbox mode
   */
  debouncedSaveAndRefresh() {
    console.log(
      "[BaseBuilder] debouncedSaveAndRefresh() called",
      new Error().stack,
    );
    clearTimeout(this.previewTimeout);
    this.previewTimeout = setTimeout(() => {
      this.saveAndRefresh();
    }, 500);
  }

  // ==========================================
  // Sandbox Mode - Draft Preview
  // ==========================================

  /**
   * Mark the configuration as having unsaved changes
   */
  markDirty() {
    this.hasUnsavedChanges = true;
    this.updateSaveButtonState();
  }

  /**
   * Build draft config object from current state for preview
   */
  buildDraftConfig() {
    const config = {
      columns: this.columns,
      filters: this.filters,
      groupings: this.groupings,
      sorts: this.sorts,
      visualization: this.visualization,
      name: this.hasNameFieldTarget ? this.nameFieldTarget.value : null
    };

    // Include scope_type if the target exists (for filter lookup URLs)
    if (this.hasScopeTypeTarget) {
      config.scope_type = this.scopeTypeTarget.value || null;
    }

    // Include parameters so dynamic (q_<id>) fields resolve against the draft selection
    if (this.parameters && Object.keys(this.parameters).length > 0) {
      config.parameters = this.parameters;
    }

    return config;
  }

  /**
   * Refresh preview with draft configuration (sandbox mode)
   * This does NOT persist changes - preview shows what the report would look like
   */
  refreshPreviewWithDraft() {
    if (!this.entityId) return;

    this.markDirty();
    this.showPreviewLoading();

    const frame = document.getElementById(this.previewFrameId);
    if (frame && this.hasPreviewUrlValue && this.previewUrlValue) {
      const url = new URL(this.previewUrlValue, window.location.origin);
      url.searchParams.set("page", this.currentPage);
      url.searchParams.set("per_page", this.currentPerPage);

      // Add draft config for sandbox preview
      url.searchParams.set(
        "draft_config",
        JSON.stringify(this.buildDraftConfig()),
      );

      // Add ephemeral sort params
      if (this.ephemeralSort) {
        url.searchParams.set("sort_field", this.ephemeralSort.field_key);
        url.searchParams.set("sort_direction", this.ephemeralSort.direction);
      }

      // Add ephemeral filters (as JSON)
      if (this.ephemeralFilters.length > 0) {
        url.searchParams.set(
          "ephemeral_filters",
          JSON.stringify(this.ephemeralFilters),
        );
      }

      frame.src = url.toString();
    }
  }

  /**
   * Debounced version of refreshPreviewWithDraft for text inputs
   */
  debouncedRefreshPreview() {
    clearTimeout(this.previewTimeout);
    this.previewTimeout = setTimeout(() => {
      this.refreshPreviewWithDraft();
    }, 500);
  }

  /**
   * Update the save button enabled/disabled state based on unsaved changes
   */
  updateSaveButtonState() {
    const dirty = String(this.hasUnsavedChanges);
    if (this.element.dataset.uiEffectsDirty !== dirty) {
      this.element.dataset.uiEffectsDirty = dirty;
      this.dispatch("editor-state-changed", { prefix: "ui-effects" });
    }
    const saveButton = this.element.querySelector('[data-action*="save"]');
    if (saveButton) {
      if (this.hasUnsavedChanges) {
        // Enable button when there are unsaved changes
        saveButton.disabled = false;
        saveButton.classList.remove("btn-disabled");
      } else {
        // Disable button when no changes to save
        saveButton.disabled = true;
        saveButton.classList.add("btn-disabled");
      }
    }
  }

  // ==========================================
  // Pagination
  // ==========================================

  paginationTargetConnected(target) {
    // The server result includes the effective size after applying its cap.
    // Re-read it whenever Turbo replaces the preview without reconnecting us.
    const page = Number.parseInt(target.dataset.page, 10);
    const perPage = Number.parseInt(target.dataset.perPage, 10);
    if (page > 0) this.currentPage = page;
    if (perPage > 0) this.currentPerPage = perPage;
  }

  nextPage(event) {
    event.preventDefault();
    const page = parseInt(event.currentTarget.dataset.page);
    if (!isNaN(page)) {
      this.currentPage = page;
      this.refreshPreviewWithParams();
    }
  }

  previousPage(event) {
    event.preventDefault();
    const page = parseInt(event.currentTarget.dataset.page);
    if (!isNaN(page)) {
      this.currentPage = page;
      this.refreshPreviewWithParams();
    }
  }

  changePerPage(event) {
    const perPage = parseInt(event.target.value);
    if (!isNaN(perPage)) {
      this.currentPerPage = perPage;
      this.currentPage = 1; // Reset to first page when changing page size
      this.refreshPreviewWithParams();
    }
  }

  // ==========================================
  // Ephemeral Column Sorting
  // ==========================================

  /**
   * Toggle sort on a column when clicking the header
   * Cycle: no sort → ascending → descending → no sort
   */
  toggleColumnSort(event) {
    // Don't toggle if clicking on action buttons or drag handle
    if (
      event.target.closest("button") ||
      event.target.closest('[data-action*="removeColumn"]')
    ) {
      return;
    }

    const fieldKey = event.currentTarget.dataset.fieldKey;
    if (!fieldKey) return;

    // Determine next sort state
    if (!this.ephemeralSort || this.ephemeralSort.field_key !== fieldKey) {
      // Not sorted or sorted by different field → sort ascending
      this.ephemeralSort = { field_key: fieldKey, direction: "asc" };
    } else if (this.ephemeralSort.direction === "asc") {
      // Currently ascending → sort descending
      this.ephemeralSort = { field_key: fieldKey, direction: "desc" };
    } else {
      // Currently descending → clear sort
      this.ephemeralSort = null;
    }

    // Reset to first page when sorting changes
    this.currentPage = 1;

    // Refresh preview with new sort
    this.refreshPreviewWithParams();
  }

  /**
   * Clear ephemeral sort
   */
  clearEphemeralSort() {
    this.ephemeralSort = null;
    this.currentPage = 1;
    this.refreshPreviewWithParams();
  }

  /**
   * Refresh preview with pagination, ephemeral sort, and ephemeral filter params
   * Also includes draft_config to preserve unsaved visualization type
   */
  refreshPreviewWithParams() {
    this.showPreviewLoading();

    const frame = document.getElementById(this.previewFrameId);
    if (frame && this.hasPreviewUrlValue && this.previewUrlValue) {
      const url = new URL(this.previewUrlValue, window.location.origin);
      url.searchParams.set("page", this.currentPage);
      url.searchParams.set("per_page", this.currentPerPage);

      // Read-only controls operate on saved settings; their empty editor
      // arrays are not an intentional draft that should replace those settings.
      if (!this.readOnlyValue) {
        url.searchParams.set(
          "draft_config",
          JSON.stringify(this.buildDraftConfig()),
        );
      }

      // Add ephemeral sort params
      if (this.ephemeralSort) {
        url.searchParams.set("sort_field", this.ephemeralSort.field_key);
        url.searchParams.set("sort_direction", this.ephemeralSort.direction);
      }

      // Add ephemeral filters (as JSON)
      if (this.ephemeralFilters.length > 0) {
        url.searchParams.set(
          "ephemeral_filters",
          JSON.stringify(this.ephemeralFilters),
        );
      }

      frame.src = url.toString();
    }
  }

  // Legacy method name for backwards compatibility
  refreshPreviewWithPagination() {
    this.refreshPreviewWithParams();
  }

  // ==========================================
  // Ephemeral Filters (Quick Filters)
  // ==========================================

  /**
   * Add an ephemeral filter from the quick filter UI
   */
  addEphemeralFilter(event) {
    event.preventDefault();

    const fieldKey = event.currentTarget.dataset.fieldKey;
    const operator = event.currentTarget.dataset.operator || "eq";
    const value = event.currentTarget.dataset.value || "";

    if (!fieldKey) return;

    // Add the filter (allow duplicates for different operators/values)
    this.ephemeralFilters.push({
      field_key: fieldKey,
      operator: operator,
      value: value
    });

    // Reset to first page
    this.currentPage = 1;

    // Refresh preview with new filters
    this.refreshPreviewWithParams();
  }

  /**
   * Update an existing ephemeral filter value
   */
  updateEphemeralFilter(event) {
    const index = parseInt(event.currentTarget.dataset.filterIndex);
    const field = event.currentTarget.dataset.filterField;
    const value = event.currentTarget.value;

    if (isNaN(index) || !this.ephemeralFilters[index]) return;

    this.ephemeralFilters[index][field] = value;

    // Reset to first page on filter change
    this.currentPage = 1;

    // Debounce the refresh for text inputs
    clearTimeout(this.ephemeralFilterTimeout);
    this.ephemeralFilterTimeout = setTimeout(() => {
      this.refreshPreviewWithParams();
    }, 300);
  }

  /**
   * Remove an ephemeral filter
   */
  removeEphemeralFilter(event) {
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.filterIndex);

    if (isNaN(index) || index < 0 || index >= this.ephemeralFilters.length)
      return;

    this.ephemeralFilters.splice(index, 1);
    this.currentPage = 1;
    this.refreshPreviewWithParams();
  }

  /**
   * Clear all ephemeral filters
   */
  clearEphemeralFilters() {
    this.ephemeralFilters = [];
    this.currentPage = 1;
    this.refreshPreviewWithParams();
  }

  /**
   * Check if any ephemeral filters are active
   */
  get hasEphemeralFilters() {
    return this.ephemeralFilters.length > 0;
  }

  /**
   * Add a quick filter to the sidebar with the operator from the clicked element
   * Filter appears in sidebar with field pre-selected, value input focused for immediate typing
   */
  promptQuickFilter(event) {
    if (this.activeColumnIndex === null) return;
    const col = this.columns[this.activeColumnIndex];
    if (!col) return;

    // Get operator from data attribute, default to 'contains'
    const operator =
      event?.target?.closest("[data-operator]")?.dataset?.operator ||
      "contains";

    this.hideColumnOptions();

    // Add to persistent filters with field and operator pre-selected, empty value
    const filterIndex = this.filters.length;
    this.filters.push({
      field_key: col.field_key,
      relationship_path: col.relationship_path || null,
      operator: operator,
      value: "",
      enabled: true
    });

    this.syncConfigs();
    this.refreshFiltersFrame();

    // Open sidebar on mobile and focus the new filter's value input
    this.showFiltersAndFocusInput(filterIndex);
  }

  /**
   * Add a quick filter to the sidebar with "equals" operator
   * Filter appears in sidebar with field pre-selected, value input focused for immediate typing
   */
  promptQuickFilterEquals() {
    if (this.activeColumnIndex === null) return;
    const col = this.columns[this.activeColumnIndex];
    if (!col) return;

    this.hideColumnOptions();

    // Add to persistent filters with field and operator pre-selected, empty value
    const filterIndex = this.filters.length;
    this.filters.push({
      field_key: col.field_key,
      relationship_path: col.relationship_path || null,
      operator: "eq",
      value: "",
      enabled: true
    });

    this.syncConfigs();
    this.refreshFiltersFrame();

    // Open sidebar on mobile and focus the new filter's value input
    this.showFiltersAndFocusInput(filterIndex);
  }

  /**
   * Open the sidebar/drawer (for mobile) and focus the value input for the specified filter
   */
  showFiltersAndFocusInput(filterIndex) {
    // Open the drawer on mobile (checkbox toggle)
    const drawerCheckbox =
      document.getElementById("report-config-drawer") ||
      document.getElementById("metric-config-drawer");
    if (drawerCheckbox && !drawerCheckbox.checked) {
      drawerCheckbox.checked = true;
    }

    // Scroll to filters section
    const filtersSection = this.element.querySelector("[data-filters-section]");
    if (filtersSection) {
      filtersSection.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    // Focus the value input after the filters frame refreshes
    // Use a small delay to allow the DOM to update
    setTimeout(() => {
      const filterElement = this.element.querySelector(
        `[data-filter-index="${filterIndex}"]`,
      );
      if (filterElement) {
        // Find the value input within this filter (text input, date input, or select)
        const valueInput = filterElement.querySelector(
          'input[data-filter-field="value"], input[type="text"], input[type="date"], input[type="number"]',
        );
        if (valueInput) {
          valueInput.focus();
          valueInput.select();
        }
      }
    }, 150);
  }

  /**
   * Add a quick filter for "is empty" to the sidebar (no value input needed)
   */
  addQuickFilterIsEmpty() {
    if (this.activeColumnIndex === null) return;
    const col = this.columns[this.activeColumnIndex];
    if (!col) return;

    this.hideColumnOptions();

    // Add to persistent filters - no value input needed for is_null operator
    this.filters.push({
      field_key: col.field_key,
      relationship_path: col.relationship_path || null,
      operator: "is_null",
      value: "",
      enabled: true
    });

    this.syncConfigs();
    this.refreshFiltersFrame();

    // Refresh preview immediately since no user input is needed
    this.debouncedRefreshPreview();
  }

  /**
   * Add a quick filter for "is not empty" to the sidebar (no value input needed)
   */
  addQuickFilterIsNotEmpty() {
    if (this.activeColumnIndex === null) return;
    const col = this.columns[this.activeColumnIndex];
    if (!col) return;

    this.hideColumnOptions();

    // Add to persistent filters - no value input needed for is_not_null operator
    this.filters.push({
      field_key: col.field_key,
      relationship_path: col.relationship_path || null,
      operator: "is_not_null",
      value: "",
      enabled: true
    });

    this.syncConfigs();
    this.refreshFiltersFrame();

    // Refresh preview immediately since no user input is needed
    this.debouncedRefreshPreview();
  }

  /**
   * Add a quick filter for "today" date range (no value input needed)
   */
  addQuickFilterToday() {
    this._addDateQuickFilter("today");
  }

  /**
   * Add a quick filter for "this week" date range (no value input needed)
   */
  addQuickFilterThisWeek() {
    this._addDateQuickFilter("this_week");
  }

  /**
   * Add a quick filter for "this month" date range (no value input needed)
   */
  addQuickFilterThisMonth() {
    this._addDateQuickFilter("this_month");
  }

  /**
   * Add a quick filter for "this year" date range (no value input needed)
   */
  addQuickFilterThisYear() {
    this._addDateQuickFilter("this_year");
  }

  /**
   * Helper to add date-based quick filters
   */
  _addDateQuickFilter(operator) {
    if (this.activeColumnIndex === null) return;
    const col = this.columns[this.activeColumnIndex];
    if (!col) return;

    this.hideColumnOptions();

    this.filters.push({
      field_key: col.field_key,
      relationship_path: col.relationship_path || null,
      operator: operator,
      value: "",
      enabled: true
    });

    this.syncConfigs();
    this.refreshFiltersFrame();
    this.debouncedRefreshPreview();
  }

  /**
   * Add a quick filter for boolean true (no value input needed)
   */
  addQuickFilterTrue() {
    this._addBooleanQuickFilter(true);
  }

  /**
   * Add a quick filter for boolean false (no value input needed)
   */
  addQuickFilterFalse() {
    this._addBooleanQuickFilter(false);
  }

  /**
   * Helper to add boolean quick filters
   */
  _addBooleanQuickFilter(value) {
    if (this.activeColumnIndex === null) return;
    const col = this.columns[this.activeColumnIndex];
    if (!col) return;

    this.hideColumnOptions();

    this.filters.push({
      field_key: col.field_key,
      relationship_path: col.relationship_path || null,
      operator: "eq",
      value: value,
      enabled: true
    });

    this.syncConfigs();
    this.refreshFiltersFrame();
    this.debouncedRefreshPreview();
  }

  /**
   * Add a persistent filter for the active column
   * This adds to filters_config (persisted on save) rather than ephemeral filters
   */
  addPersistentFilter() {
    if (this.activeColumnIndex === null) return;
    const col = this.columns[this.activeColumnIndex];
    if (!col) return;

    this.hideColumnOptions();

    // Add to persistent filters with field pre-selected
    this.filters.push({
      field_key: col.field_key,
      field_type: col.field_type,
      relationship_path: col.relationship_path || null,
      operator: this.defaultOperatorForFieldType(col.field_type),
      value: "",
      enabled: true
    });

    this.syncConfigs();
    this.refreshFiltersFrame();

    // Scroll to filters section
    const filtersSection = this.element.querySelector("[data-filters-section]");
    if (filtersSection) {
      filtersSection.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  /**
   * Get field label from available fields
   */
  getFieldLabel(fieldKey) {
    if (!this.hasAvailableFieldsValue || !this.availableFieldsValue) {
      return fieldKey;
    }

    // Search in fields
    const fields = this.availableFieldsValue.fields || [];
    const foundField = fields.find((f) => f.key === fieldKey);
    if (foundField) return foundField.label || fieldKey;

    // Search in relationships
    const relationships = this.availableFieldsValue.relationships || [];
    for (const rel of relationships) {
      const field = (rel.fields || []).find((f) => f.key === fieldKey);
      if (field) return field.label || fieldKey;
    }

    return fieldKey;
  }

  // ==========================================
  // Preview (Turbo Frame)
  // ==========================================

  refreshPreview() {
    const frame = document.getElementById(this.previewFrameId);
    if (frame) {
      if (this.hasPreviewUrlValue && this.previewUrlValue) {
        // Include pagination params in preview URL
        const url = new URL(this.previewUrlValue, window.location.origin);
        url.searchParams.set("page", this.currentPage);
        url.searchParams.set("per_page", this.currentPerPage);
        frame.src = url.toString();
      }
    }
  }

  showPreviewLoading() {
    const preview = this.element.querySelector(`#${this.previewFrameId}`);
    if (preview) {
      preview.classList.add("opacity-50", "pointer-events-none");
    }

    // Add loading spinner
    if (
      this.hasPreviewFrameTarget &&
      !this.previewFrameTarget.querySelector(".preview-loading-spinner")
    ) {
      const spinner = document.createElement("div");
      spinner.className =
        "preview-loading-spinner absolute inset-0 flex items-center justify-center bg-base-200/50 z-10";
      spinner.innerHTML =
        '<span class="loading loading-spinner loading-lg text-primary"></span>';
      this.previewFrameTarget.classList.add("relative");
      this.previewFrameTarget.appendChild(spinner);
    }
  }

  hidePreviewLoading() {
    const preview = this.element.querySelector(`#${this.previewFrameId}`);
    if (preview) {
      preview.classList.remove("opacity-50", "pointer-events-none");
    }

    const spinner = this.element.querySelector(".preview-loading-spinner");
    if (spinner) {
      spinner.remove();
    }
  }

  // Called when the Turbo Frame finishes loading
  previewLoaded() {
    this.hidePreviewLoading();
  }
}
